/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { catchError, filter, mergeMap, skip, take } from 'rxjs/operators';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { GroupSharedService } from '../../../services/shared/group-shared/group-shared.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { Store } from '@ngrx/store';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { UserActionTypes } from '../../../store/user/user.action';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { InterventionInstanceReminderInterface } from '../../../models/interface/intervention-instance-reminder.interface';
import { ReminderActionTypes } from '../../../store/reminder/reminder.action';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

/**
 * Component:
 * Group instances page displaying a list of intervention instances of the group;
 * Can be found: {uri}/groups/{{group_id}}/instances
 */
@Component({
  selector: 'app-group-instance',
  templateUrl: './group-instance.component.html',
  styleUrls: ['./group-instance.component.scss'],
  providers: [InterventionInstanceStore, StudyStore],
})
export class GroupInstanceComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;

  public isLoading$: Observable<boolean>;

  // Data provided by UserService
  public users: UserInterface[] = [];
  public studyCollaborators: UserInterface[] = [];

  // Default - Filter
  public filter = { searchFilter: '' };
  public excludeCanceledInstance = true;

  // Translation for study name
  public param = { intervention_name: '...' };

  // Subject and Observable
  public selectedUserSubject = new BehaviorSubject<number>(null);

  public instancesData$: Observable<PaginationData<InterventionInstanceInterface>>;
  public instances$: Observable<InterventionInstanceInterface[]>;
  public isCollapse$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public pagedInstances$: Observable<InterventionInstanceInterface[]>;

  public allInterventionInstanceReminders$: Observable<InterventionInstanceReminderInterface[]>;

  public study: StudyInterface;

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private allMyInstancesOfStudy$: Observable<InterventionInstanceInterface[]>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private studyMembers$: Observable<UserInterface[]>;

  private groups$: Observable<StudyInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private myInstances: InterventionInstanceInterface[] = [];
  private instancesDataSubject = new BehaviorSubject<PaginationData<InterventionInstanceInterface>>({ items: [] });
  private instancesSubject = new BehaviorSubject<InterventionInstanceInterface[]>([]);
  private searchTextSubject = new BehaviorSubject<string>('');

  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private myMembers$: Observable<UserInterface[]>;

  private pagedInstancesSubject = new BehaviorSubject<InterventionInstanceInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: GroupSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private interventionInstanceStore: InterventionInstanceStore,
    private studyStore: StudyStore,
    private store: Store<{
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
      myMembers: UserInterface[];
      allInterventionInstanceReminders: InterventionInstanceReminderInterface[];
      collabGroups: StudyInterface[];
    }>,
  ) {
    this.allInterventionInstanceReminders$ = this.store.select('allInterventionInstanceReminders');
    this.myMembers$ = this.store.select('myMembers');
    this.groups$ = this.store.select('collabGroups');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.instancesData$ = this.instancesDataSubject.asObservable();
    this.instances$ = this.instancesSubject.asObservable();
    this.pagedInstances$ = this.pagedInstancesSubject.asObservable();
    this.allMyInstancesOfStudy$ = this.interventionInstanceStore.allMyInstancesOfStudy$;
    this.studyMembers$ = this.studyStore.studyMembers$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.sharedService.sourceGroup$
        .pipe(
          filter((value) => !!value),
          take(1)
        )
        .subscribe((study) => {
          this.study = study;
          this.applyInstanceFilter();
          this.param = { intervention_name: study.attributes.name };
        }),
    );

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.applyInstanceFilter();
      })
    );
  }

  public applyInstanceFilter(setFirst?: boolean): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadInstanceList(setFirst).subscribe((_result) => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public reloadInstanceList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    this.store.dispatch({
      type: ReminderActionTypes.getAllInterventionInstanceRemindersOfUserType,
      payload: {},
    });
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { include: 'owners,roles' },
    });
    return this.groups$.pipe(
      skip(1),
      take(1),
      mergeMap((studies) => {
        if (studies.length > 0) {
          const myRoleSlug = this.helper.getHighestRoleOfStudy(
            studies.find((study) => study.id.toString() === this.study.id.toString()),
          );
          if (myRoleSlug === 'study.ecoachmanager' || myRoleSlug === 'study.owner') {
            this.isManagerSubject.next(true);
          }
        }
        if (this.isManagerSubject.value) {
          this.interventionInstanceStore.getAllInstancesOfStudy({ studyId: this.study.id });
        } else {
          this.interventionInstanceStore.getMyInstancesOfStudy({ studyId: this.study.id });
        }
        return this.allMyInstancesOfStudy$.pipe(
          skip(1),
          take(1),
          mergeMap((myInstances: InterventionInstanceInterface[]) => {
            this.myInstances = this.excludeCanceledInstance
              ? myInstances.filter((instance) => instance.attributes.progress.current_state !== 'canceled')
              : myInstances;
            if (this.isManagerSubject.value) {
              this.studyStore.getMembers({ studyId: this.study.id });
            } else {
              this.studyStore.getECoachMembers({ studyId: this.study.id });
            }
            const studyMembers = this.studyMembers$.pipe(skip(1), take(1));
            this.store.dispatch({
              type: StudyActionTypes.getCollaboratorsType,
              payload: { studyId: this.study.id, include: 'roles' },
            });
            this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.study.id));
            const collaboratorsOfStudy = this.collaboratorsOfStudy$.pipe(
              filter((collaboratorsOfStudy) => !!collaboratorsOfStudy),
              take(1),
            );
            let myMembers: Observable<UserInterface[] | null>;
            if (!this.isManagerSubject.value) {
              this.store.dispatch({
                type: UserActionTypes.getMyMembersType,
                payload: {},
              });
              myMembers = this.myMembers$.pipe(skip(1), take(1));
            } else {
              myMembers = of(null);
            }
            return forkJoin({ studyMembers, collaboratorsOfStudy, myMembers });
          }),
        );
      }),
      mergeMap((results) => {
        this.studyCollaborators = results.collaboratorsOfStudy.collaborators;
        this.users = results.studyMembers;
        if (results.myMembers !== null) {
          this.users = this.users.filter(
            (user) => results.myMembers.some((member) => member.id === user.id),
          );
        }
        this.selectedUserSubject.next(this.users.length > 0 ? this.users[0].id : null);
        return this.performFilterAndPagination(!!setFirst, pageIndex, pageSize);
      }),
      catchError(() => {
        this.users = [];
        this.studyCollaborators = [];
        return this.performFilterAndPagination(!!setFirst, pageIndex, pageSize);
      })
    );
  }

  public updatePagedInterventionInstances(event: InterventionInstanceInterface[]) {
    if (event) {
      this.pagedInstancesSubject.next(event);
    }
  }

  public openDialogGroupInterventionInstanceCreate(): void {
    this.helperDialog
      .openDialogAccountInterventionInstanceAssignment(
        'studyInvitation',
        undefined,
        this.study,
        this.users.find((user) => user.id.toString() === this.selectedUserSubject.value.toString()),
        this.users,
      )
      .afterClosed()
      .subscribe(() => this.applyInstanceFilter());
  }

  public openDialogAddCollaboratorToECoachInstancesStudy(): void {
    this.helperDialog
      .openDialogAddCollaboratorToECoachInstancesStudy(this.study.id)
      .afterClosed()
      .subscribe(() => this.applyInstanceFilter());
  }

  public performFilterAndPagination(setFirst: boolean, pageIndex: number, pageSize: number): Observable<boolean> {
    return this.helper
      .searchInterventionInstanceByInput(
        this.filter.searchFilter,
        this.searchTextSubject,
        this.myInstances,
        this.instancesSubject,
        this.study.id,
        this.studyCollaborators,
        this.users,
      )
      .pipe(
        mergeMap(
          () => this.helper.setPagedContent(
            this.instancesSubject,
            this.pagedInstancesSubject,
            setFirst,
            pageIndex,
            pageSize,
            this.instancesDataSubject,
          ),
        ),
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
